import { Injectable } from '@angular/core';

import { DataReactionWithRelations } from '@osos/shared/api';
import { ParserBase } from '../../bases/parser/parser-base';

import {
  DATA_QUERY_ID,
  DataReactionWithRelationsPresenterModel,
  EventExecutorTypeEnum,
  EventTypeEnum,
  ExpressionTypeEnum,
} from '@osos/shared/types';
import { DataEntityActionFactoryService } from '../../factories/data-entity-action-factory/data-entity-action-factory.service';

@Injectable()
export class DataEntityTiggerParserV1Service extends ParserBase<
  DataReactionWithRelations,
  DataReactionWithRelationsPresenterModel
> {
  constructor(
    private dataEntityActionFactoryService: DataEntityActionFactoryService,
  ) {
    super(
      DataReactionWithRelations,
      DataReactionWithRelationsPresenterModel,
      1,
    );
  }

  getPresenterModel(
    model: DataReactionWithRelations,
  ): DataReactionWithRelationsPresenterModel {
    const presenter = {} as DataReactionWithRelationsPresenterModel;
    if (Array.isArray(model.actions)) {
      presenter.actions = model.actions.map((action) =>
        this.dataEntityActionFactoryService.createPresenterModel(action),
      );
    }
    presenter.id = model.id;
    presenter.name = model.name;
    presenter.eventType = model.eventType;
    presenter.criteria = model.criteria && {
      args: model.criteria.args,
      operator: model.criteria.operator as ExpressionTypeEnum,
      ...(!!model.criteria.field && {
        field: model.criteria.field,
      }),
    };
    if (
      presenter.eventType === EventTypeEnum.WorkflowStepExited ||
      presenter.eventType === EventTypeEnum.WorkflowStepReached ||
      presenter.eventType === EventTypeEnum.WorkflowActionExecuted ||
      presenter.eventType === EventTypeEnum.SlaTimeExceeded
    ) {
      presenter.eventConfig = model.eventConfig?.length
        ? model.eventConfig
        : [
            {
              relatedInfo: undefined,
              steps: [],
            },
          ];
    } else {
      presenter.eventConfig = model.eventConfig;
    }
    presenter.query = model.query?.dataEntry;
    presenter.isImplicit = model.isImplicit;
    presenter.disabled = model.disabled ?? false;
    presenter.loopThreshold = model.loopThreshold;
    presenter.spaceScope =
      this.dataEntityActionFactoryService.parseTriggerSpaceScopeEnum(
        model.spaceScope,
      );
    const executorType = model.executorType?.id as EventExecutorTypeEnum;
    presenter.executorType = Object.values(EventExecutorTypeEnum).includes(
      executorType,
    )
      ? executorType
      : EventExecutorTypeEnum.MANAGER;
    presenter.type = model.type;
    presenter.sourceEntity = model.sourceEntity;
    presenter.updateIdentifier = model.updateIdentifier;
    presenter.specificationVersion = model.specificationVersion;

    return presenter;
  }

  getDataModel(
    presenter: DataReactionWithRelationsPresenterModel,
  ): DataReactionWithRelations {
    const executorType = presenter.executorType;
    const validExecutorType = Object.values(EventExecutorTypeEnum).includes(
      executorType,
    )
      ? executorType
      : EventExecutorTypeEnum.MANAGER;
    const model = {
      id: presenter.id,
      name: presenter.name,
      eventType: presenter.eventType,
      actions: presenter.actions?.map((a) =>
        this.dataEntityActionFactoryService.createDataModel(a),
      ),
      criteria: presenter.criteria ?? undefined,
      eventConfig: presenter.eventConfig ?? undefined,
      isImplicit: presenter.isImplicit,
      disabled: presenter.disabled ?? false,
      loopThreshold: presenter.loopThreshold,
      query: presenter.query
        ? {
            dataEntity: DATA_QUERY_ID,
            dataEntry: presenter.query,
          }
        : undefined,
      spaceScope:
        this.dataEntityActionFactoryService.parseTriggerScopeEnumToModel(
          presenter.spaceScope,
        ),
      executorType: {
        id: validExecutorType,
      },
      type: presenter.type,
      sourceEntity: presenter.sourceEntity,
      specificationVersion: presenter.specificationVersion,
      updateIdentifier: presenter.updateIdentifier,
    } as DataReactionWithRelations;

    return model;
  }
}
